import { Close } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useMessage } from '../../../../base/MessageProvider';
import ReactTextInput from '../../../../components/ControllerComponent/ReactTextInput';
import clsx from 'clsx';
import { getProfileData, updateProfile } from '../../../actions/profile';
import { Bars } from 'react-loader-spinner';
import ReactSelect from '../../../../components/ControllerComponent/ReactSelect';
import { isValidJson, replaceCountryCode } from '../../../../utils';
import { contactPrivacyOptions, extraCurricularOptions, hairTypesOptions, heightUnitOptions, languagesOptions, nationalityOptions } from '../../../../constants';
import { useParams } from 'react-router-dom';
import { FormTypes } from '../TabPanels/StatsTab';
import ReactLocationInput from '../../../../components/ControllerComponent/ReactLocationInput';
import ReactTelInput from '../../../../components/ControllerComponent/ReactTelInput';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    maxHeight: 600,
    width: '90%',
    bgcolor: 'white',
    paddingX: 3.5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validateSchema = Yup.object().shape({
    bio: Yup.string().max(600, "Bio data must be smaller than 600"),
    locations: Yup.array().max(6, 'Maximum 6 locations can be added'),
    height: Yup.string().test("num", "Invalid height", (value) => !isNaN(+value)).test("positive", "Height must be greater than 0", (value) => !value?.length || +value > 0),
    weight: Yup.string().test("num", "Invalid weight", (value) => !isNaN(+value)).test("positive", "Weight must be greater than 0", (value) => !value?.length || +value > 0),
    businessEmail: Yup.string().email('Invalid email'),
    instagramHandle: Yup.string()
        .test("insta", "Invalid user name", (value) => !value || /^[a-z0-9_]+$/.test(value)).optional(),
    businessMobileNo: Yup.string().transform(replaceCountryCode)
        .test("length", "Mobile number must be 10 digit", (value) => value.length <= 0 || value.length === 10)
        .test("phone", "Invalid mobile number", (value) => /^[0-9]*$/.test(value)).optional(),
})

const ProfileEditModal = ({ open, onClose }) => {
    const { formId } = useParams();

    const dispatch = useDispatch();
    const alert = useMessage();
    const loading = useSelector((state) => state.artistProfileReducer?.loading);
    const profileData = useSelector((state) => state.artistProfileReducer?.profileData);
    const locationList = useSelector(state => state.artistGeneralReducer?.locationList) ?? [];

    const userProfile = profileData?.userProfile;
    const profileDetails = profileData?.userProfile?.profileDetails;

    const isAll = formId === FormTypes.STATS_INFO;

    const {
        control, handleSubmit,
        setValue, watch,
        clearErrors, reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            bio: "",
            cintaaNumber: "",
            nationality: "",
            height: "",
            heightUnit: "cm",
            weight: "",
            hairType: "",
            language: [],
            extraCuricular: [],
            locations: [],
            businessEmail: "",
            businessMobileNo: "",
            contactVisibility: "",
            instagramHandle: "",
        },
        resolver: yupResolver(validateSchema)
    });

    useEffect(() => {
        clearErrors(); reset();
        if (userProfile && open) {
            setValue("bio", userProfile?.bio ?? '');
            setValue("businessEmail", userProfile?.businessEmail ?? '');
            setValue("businessMobileNo", userProfile?.businessMobileNo ?? '');
            setValue("cintaaNumber", userProfile?.cintaaNumber ?? "");
            setValue("nationality", userProfile?.nationality ?? "");
            setValue("instagramHandle", userProfile?.instagramHandle ?? "");
        }
    }, [userProfile, open]);

    useEffect(() => {
        if (open) setValue("contactVisibility", profileData?.contactVisibility ?? "");
    }, [profileData, open]);

    useEffect(() => {
        if (profileDetails && open) {
            profileDetails?.forEach((profile) => {
                switch (profile.category) {
                    case 'Physical Stats': {
                        const data = profile.data?.find((a) => a.key === 'Height')?.value?.split(" ");
                        setValue("height", data?.[0]);
                        setValue("heightUnit", data?.[1] ?? "cm");
                        setValue("weight", profile.data?.find((a) => a.key === 'Weight')?.value);
                        setValue("hairType", profile.data?.find((a) => a.key === 'Hair Type')?.value);
                        break;
                    }
                    case 'Skills': {
                        setValue("language", profile.data?.find((a) => a.key === 'Language')?.value?.split(',')
                            .filter((s) => languagesOptions?.find((option) => option.value === s)));
                        setValue("extraCuricular", profile.data.find((a) => a.key === 'Extra Curricular')?.value?.split(',')
                            .filter((s) => extraCurricularOptions?.find((option) => option.value === s)));
                        break;
                    }
                    case 'Work Preference': {
                        const availableIn = profile.data.find((a) => a.key === 'Available In')?.value;
                        if (isValidJson(availableIn)) setValue("locations", JSON.parse(availableIn));
                        else setValue('locations', []);
                        break;
                    }
                }
            });
        }
    }, [profileDetails, open]);

    const getDataPayload = (userProfileData) => {
        let dataPayload = { userProfile: {} };
        if (isAll || formId === FormTypes.BIO_DATA) {
            dataPayload['userProfile']['bio'] = userProfileData?.bio ?? '';
        }
        if (isAll || formId === FormTypes.PHYSICAL_STATS) {
            dataPayload['userProfile']['profileDetails'] = [];
            dataPayload['userProfile']['profileDetails'].push({
                category: 'Physical Stats',
                data: [
                    { key: 'Height', value: `${userProfileData?.height} ${userProfileData.heightUnit}` ?? ''},
                    { key: 'Weight', value: userProfileData?.weight ?? ''},
                    { key: 'Hair Type', value: userProfileData?.hairType ?? ''},
                ],
            });
        }
        if (isAll || formId === FormTypes.SKILLS) {
            dataPayload['userProfile']['profileDetails'] = dataPayload?.userProfile?.profileDetails || [];
            dataPayload['userProfile']['profileDetails'].push({
                category: 'Skills',
                data: [
                    { key: 'Language', value: userProfileData?.language?.join(',') ?? '' },
                    { key: 'Extra Curricular', value: userProfileData?.extraCuricular?.join(',') ?? '' },
                ],
            });
        }
        if (isAll || formId === FormTypes.WORK_PREFERENCE) {
            dataPayload['userProfile']['profileDetails'] = dataPayload?.userProfile?.profileDetails || [];
            dataPayload['userProfile']['profileDetails'].push({
                category: 'Work Preference',
                data: [{ key: 'Available In', value: JSON.stringify(userProfileData.locations) ?? '[]' }],
            });
        }
        if (isAll || formId === FormTypes.PERSONAL_INFO) {
            dataPayload['userProfile']['nationality'] = userProfileData?.nationality ?? '';
            dataPayload['userProfile']['cintaaNumber'] = userProfileData?.cintaaNumber ?? '';
        }
        if (isAll || formId === FormTypes.CONTACT_INFORMATION) {
            dataPayload['contactVisibility'] = userProfileData.contactVisibility;
            dataPayload['userProfile']['businessMobileNo'] = replaceCountryCode(userProfileData?.businessMobileNo) ?? '';
            dataPayload['userProfile']['businessEmail'] = userProfileData?.businessEmail ?? '';
        }
        if (isAll || formId === FormTypes.SOCIAL_LINKS) {
            dataPayload['userProfile']['instagramHandle'] = userProfileData?.instagramHandle ?? '';
        }
        return dataPayload;
    }

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
        onClose();
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const onSubmit = async (data) => {
        dispatch(updateProfile(getDataPayload(data), successCallback, errorCallback));
    }

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-center text-[16px]">Edit Profile</h2>
                </Box>
                <Stack className="w-full overflow-auto pr-1" my={1} spacing={2}>
                    {(isAll || formId === FormTypes.BIO_DATA) && (
                        <ReactTextInput
                            hookFormControl={control}
                            formLabel="Bio"
                            varName="bio"
                            placeholder="Write something about yourself..."
                            errorMessage={errors?.bio?.message}
                            isMultiLine autoFocus
                            rows={6}
                            maxLength={600}
                        />
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.PERSONAL_INFO) && (
                        <Stack className="w-full" spacing={2}>
                            <ReactTextInput
                                hookFormControl={control}
                                formLabel="CINTAA No"
                                varName="cintaaNumber"
                                key={'cintaaNumber'}
                                placeholder="Enter here"
                                errorMessage={errors?.cintaaNumber?.message}
                            />
                            <ReactSelect
                                hookFormControl={control}
                                formLabel="Nationality"
                                varName="nationality"
                                key={'nationality'}
                                options={nationalityOptions.map(({ label }) => ({ label, value: label }))}
                                errorMessage={errors?.nationality?.message}
                            />
                        </Stack>
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.PHYSICAL_STATS) && (
                        <Stack className="w-full" spacing={2}>
                            <Stack alignItems={'flex-start'} direction={'row'} spacing={2}>
                                <ReactTextInput
                                    hookFormControl={control}
                                    formLabel="Height"
                                    varName="height"
                                    key={'height'}
                                    dataType="number"
                                    placeholder="Enter height"
                                    errorMessage={errors?.height?.message}
                                />
                                <ReactSelect
                                    hookFormControl={control}
                                    formLabel="Unit"
                                    varName="heightUnit"
                                    key={'heightUnit'}
                                    width="30%"
                                    withSelect={false}
                                    options={heightUnitOptions}
                                    errorMessage={errors?.heightUnit?.message}
                                />
                            </Stack>
                            <ReactTextInput
                                hookFormControl={control}
                                formLabel="Weight"
                                varName="weight"
                                key={'weight'}
                                dataType="number"
                                placeholder="Enter weight"
                                errorMessage={errors?.weight?.message}
                                endAdornment={(
                                    <h2 className="font-['Poppins-Regular'] text-[15px]">Kg</h2>
                                )}
                            />
                            <ReactSelect
                                hookFormControl={control}
                                formLabel="Hair Type"
                                placeholder="Enter hair type"
                                varName="hairType"
                                key={'hairType'}
                                options={hairTypesOptions}
                                errorMessage={errors?.hairType?.message}
                            />
                        </Stack>
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.SKILLS) && (
                        <Stack className="w-full" spacing={2}>
                            <ReactSelect
                                hookFormControl={control}
                                formLabel="Languages"
                                placeholder="Select languages"
                                varName="language"
                                isMultiSelect
                                options={languagesOptions}
                                errorMessage={errors?.language?.message}
                            />
                            <ReactSelect
                                hookFormControl={control}
                                formLabel="Exrtra Curricular"
                                placeholder="Select Options"
                                varName="extraCuricular"
                                isMultiSelect
                                options={extraCurricularOptions}
                                errorMessage={errors?.extraCuricular?.message}
                            />
                        </Stack>
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.WORK_PREFERENCE) && (
                        <Stack className="w-full" spacing={2}>
                            <ReactLocationInput
                                placeholder="Eg. Mumbai, Maharashtra"
                                formLabel="Available In*"
                                hookFormControl={control} 
                                varName="locations" 
                                multiSelect
                                errorMessage={errors?.locations?.message}
                                options={locationList} 
                            />
                        </Stack>
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.CONTACT_INFORMATION) && (
                        <Stack className="w-full" spacing={2}>
                            <ReactTextInput
                                hookFormControl={control}
                                formLabel="Email"
                                placeholder="abc@xyz.com"
                                varName="businessEmail"
                                errorMessage={errors?.businessEmail?.message}
                            />
                            <ReactTelInput
                                hookFormControl={control}
                                formLabel="Contact"
                                varName="businessMobileNo"
                                placeholder="XXXXXXXXXX"
                                errorMessage={errors?.businessMobileNo?.message}
                            />
                            <ReactSelect
                                hookFormControl={control}
                                formLabel="Contact privacy"
                                placeholder="Privacy"
                                varName="contactVisibility"
                                options={contactPrivacyOptions}
                                errorMessage={errors?.contactVisibility?.message}
                            />
                        </Stack>
                    )}
                    {isAll && <Divider className='my-3' />}
                    {(isAll || formId === FormTypes.SOCIAL_LINKS) && (
                        <Stack className="w-full" spacing={2}>
                            <ReactTextInput
                                placeholder="Eg: __starnex__"
                                formLabel="Instagram Handle"
                                hookFormControl={control} 
                                varName="instagramHandle" 
                                errorMessage={errors?.instagramHandle?.message}
                                startAdornment={
                                    <h2 className="font-['Poppins-Regular'] text-[15px] mr-1">@</h2>
                                }
                            />
                        </Stack>
                    )}
                </Stack>
                <Stack className="w-full" alignItems={'center'} direction={'row'} spacing={1}>
                    <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                        Submit
                        <Bars
                            height="20"
                            width={'20'}
                            color="#FFFFFF"
                            ariaLabel="bars-loading"
                            visible={loading}
                        />
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
}

export default React.memo(ProfileEditModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});